/*@flow*/
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { useState, useEffect, createRef } from 'react';
import { con, state } from 'ops';
import * as I from 'immutable';
import Schema, { type OptionsT } from './Schema';
import 'theme/leaflet/index.scss';
import type { DecksT, StatusT, NameT } from './types';
import { type HOC } from 'recompose';
import RenderDecks from './RenderDecks';
import LoadingBar from 'comps/LoadingBar';
import compose from 'lib/compose';

const renderIfConfigured = (Component) => (props) => {
  const isConfigured = (o: PropsT) => o.decks.size > 0 && o.schema.size > 0;
  return isConfigured(props) ? <Component {...props} /> : null;
};

const pluck = (key: string) => ({ name }) => state(`${name}.${key}`, I.Map());

type PropsT = {
  name: NameT,
  loading: StatusT,
  schema: I.Map<string, *>,
  decks: DecksT,
};

type InputPropsT = { name: string };

const Enhancer: HOC<PropsT, InputPropsT> = compose(
  con({
    schema: pluck('schema'),
    decks: pluck('decks'),
    loading: pluck('loading'),
  }),
  renderIfConfigured
);

const FolioComponent = (props) => {
  const ref = createRef();
  const { schema, decks, loading } = props;
  const [map, setMap] = useState();
  const [mapAdapter, setMapAdapter] = useState();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (mapAdapter) return;
    const adapter = new Schema(schema.toJS());
    if (!adapter) return;
    const m = adapter.create(el);
    // Global
    window[props.name] = m;
    setMap(m);
    setMapAdapter(() => adapter);
    return () => adapter && adapter.remove();
  }, []);

  useEffect(() => {
    if (!ref.current) return;
    if (!mapAdapter) return;
    const s: OptionsT = (schema.toJS(): any);
    mapAdapter.update(s);
  }, [schema.hashCode(), mapAdapter]);

  return (
    <div
      className="CDB-Map-canvas"
      css={css(`
        flex-grow: 1;
        position: relative;
        height: inherit;
    `)}
    >
      <div
        ref={ref}
        className="CDB-Map"
        css={css(`
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        border-radius: 4px;
        overflow: hidden;
        height: inherit;

        @media (max-width: 759px) {
          border-radius: 0;
        }
      `)}
      />
      <LoadingBar isVisible={loading} />
      {map && <RenderDecks map={map} decks={decks} />}
    </div>
  );
};

export default Enhancer(FolioComponent);
